import type { OrdinalMarketApprovalRole, OrdinalMarketOperation } from './ordinal-market-intent'

const PREFIX = 'treechat:ordinal-market-action:v1:'
const PROVISIONAL_PREFIX = 'treechat:ordinal-market-action-provisional:v1:'
const TTL_MS = 30 * 60 * 1000
const runtimeIntents = new Map<string, OrdinalMarketActionIntent>()
const runtimeProvisionalIntents = new Map<string, OrdinalMarketProvisionalActionIntent>()

export type OrdinalMarketActionTerms = {
    version: 1
    operation: OrdinalMarketOperation
    ordinalId: string
    priceSats: number
    treechatFeeSats: number
    sellerProceedsSats: number
    userId: string
    deviceId: string
    createdAt: number
}

export type OrdinalMarketActionIntent = OrdinalMarketActionTerms & {
    orderId: string
}

export type OrdinalMarketProvisionalActionIntent = OrdinalMarketActionTerms

/**
 * Whether a signing request's transaction is the one this device's confirmed
 * action asked for. Buying a server-key listing signs the buyer side of one
 * fixed offer-shaped transaction (mixed custody) rather than a listing purchase.
 */
export function ordinalMarketActionMatchesRequest(
    actionOperation: OrdinalMarketOperation,
    manifestOperation: OrdinalMarketOperation,
    approvalRole: OrdinalMarketApprovalRole,
): boolean {
    if (actionOperation === manifestOperation) return true
    return (
        actionOperation === 'ordinal_market_listing_purchase' &&
        manifestOperation === 'ordinal_market_offer' &&
        approvalRole === 'buyer_offer'
    )
}

function key(orderId: string): string {
    return `${PREFIX}${orderId}`
}

function provisionalKey(
    intent: Pick<
        OrdinalMarketActionTerms,
        'operation' | 'ordinalId' | 'priceSats' | 'userId' | 'deviceId'
    >,
): string {
    return `${PROVISIONAL_PREFIX}${[
        intent.userId,
        intent.deviceId,
        intent.ordinalId,
        intent.operation,
        intent.priceSats,
    ]
        .map(value => encodeURIComponent(String(value)))
        .join(':')}`
}

function validTerms(intent: OrdinalMarketActionTerms): boolean {
    return (
        intent.version === 1 &&
        !!intent.ordinalId &&
        !!intent.userId &&
        !!intent.deviceId &&
        Number.isSafeInteger(intent.priceSats) &&
        intent.priceSats > 0 &&
        Number.isSafeInteger(intent.treechatFeeSats) &&
        intent.treechatFeeSats >= 0 &&
        Number.isSafeInteger(intent.sellerProceedsSats) &&
        intent.sellerProceedsSats >= 0 &&
        intent.treechatFeeSats + intent.sellerProceedsSats === intent.priceSats &&
        Number.isSafeInteger(intent.createdAt)
    )
}

function current(intent: OrdinalMarketActionTerms): boolean {
    return (
        validTerms(intent) &&
        intent.createdAt <= Date.now() &&
        Date.now() - intent.createdAt <= TTL_MS
    )
}

export function saveOrdinalMarketActionIntent(intent: OrdinalMarketActionIntent): void {
    if (!validTerms(intent) || !intent.orderId || intent.createdAt > Date.now()) {
        throw new Error('Invalid ordinal market action intent')
    }
    const saved = { ...intent }
    runtimeIntents.set(intent.orderId, saved)
    try {
        localStorage.setItem(key(intent.orderId), JSON.stringify(saved))
    } catch {
        // The active page can still complete the request. A remount requires
        // working localStorage, so the signer will fail closed after one.
    }
}

export function loadOrdinalMarketActionIntent(orderId: string): OrdinalMarketActionIntent | null {
    const runtime = runtimeIntents.get(orderId)
    if (runtime && current(runtime) && runtime.orderId === orderId) return { ...runtime }
    let serialized: string | null = null
    try {
        serialized = localStorage.getItem(key(orderId))
    } catch {
        return null
    }
    if (!serialized) return null
    try {
        const intent = JSON.parse(serialized) as OrdinalMarketActionIntent
        if (!current(intent) || intent.orderId !== orderId || !intent.orderId) {
            throw new Error('Expired ordinal market action intent')
        }
        return intent
    } catch {
        clearOrdinalMarketActionIntent(orderId)
        return null
    }
}

export function saveProvisionalOrdinalMarketActionIntent(
    intent: OrdinalMarketProvisionalActionIntent,
): void {
    if (!validTerms(intent) || intent.createdAt > Date.now()) {
        throw new Error('Invalid provisional ordinal market action intent')
    }
    const storageKey = provisionalKey(intent)
    const saved = { ...intent }
    runtimeProvisionalIntents.set(storageKey, saved)
    try {
        localStorage.setItem(storageKey, JSON.stringify(saved))
    } catch {}
}

function loadProvisionalOrdinalMarketActionIntent(
    expected: OrdinalMarketProvisionalActionIntent,
): OrdinalMarketProvisionalActionIntent | null {
    const storageKey = provisionalKey(expected)
    let intent = runtimeProvisionalIntents.get(storageKey) || null
    if (!intent) {
        try {
            const serialized = localStorage.getItem(storageKey)
            intent = serialized
                ? (JSON.parse(serialized) as OrdinalMarketProvisionalActionIntent)
                : null
        } catch {
            return null
        }
    }
    if (!intent || !current(intent)) {
        clearProvisionalOrdinalMarketActionIntent(expected)
        return null
    }
    if (
        intent.operation !== expected.operation ||
        intent.ordinalId !== expected.ordinalId ||
        intent.priceSats !== expected.priceSats ||
        intent.treechatFeeSats !== expected.treechatFeeSats ||
        intent.sellerProceedsSats !== expected.sellerProceedsSats ||
        intent.userId !== expected.userId ||
        intent.deviceId !== expected.deviceId
    ) {
        return null
    }
    return intent
}

export function bindProvisionalOrdinalMarketActionIntent(
    orderId: string,
    expected: OrdinalMarketProvisionalActionIntent,
): OrdinalMarketActionIntent | null {
    const provisional = loadProvisionalOrdinalMarketActionIntent(expected)
    if (!provisional) return null
    const bound = { ...provisional, orderId }
    saveOrdinalMarketActionIntent(bound)
    clearProvisionalOrdinalMarketActionIntent(provisional)
    return bound
}

export function clearProvisionalOrdinalMarketActionIntent(
    intent: Pick<
        OrdinalMarketActionTerms,
        'operation' | 'ordinalId' | 'priceSats' | 'userId' | 'deviceId'
    >,
): void {
    const storageKey = provisionalKey(intent)
    runtimeProvisionalIntents.delete(storageKey)
    try {
        localStorage.removeItem(storageKey)
    } catch {}
}

export function clearOrdinalMarketActionIntent(orderId: string): void {
    runtimeIntents.delete(orderId)
    try {
        localStorage.removeItem(key(orderId))
    } catch {}
}
